'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  Kanban,
  CheckSquare,
  FileCheck,
  Users,
  GitBranch,
  BarChart3,
  Share2,
  CheckCircle2,
  ArrowRight,
  Zap,
  Shield,
  RefreshCw,
  FileText
} from 'lucide-react'

import { FadeUp } from '@/components/ui/fade-up'
const features = [
  {
    icon: Kanban,
    title: 'カンバンボード',
    description: '直感的なドラッグ&ドロップで、タスクの状態を可視化。チーム全体の進捗を一目で把握。'
  },
  {
    icon: GitBranch,
    title: '承認フロー',
    description: '多段階承認、条件分岐、自動エスカレーション。複雑な意思決定プロセスも簡単に。'
  },
  {
    icon: Share2,
    title: '外部共有',
    description: '関係者への進捗共有を安全に。権限管理で必要な情報だけを公開。'
  },
  {
    icon: FileCheck,
    title: '成果物管理',
    description: 'ドキュメント、画像、動画など全ての成果物をプロジェクトに紐付けて一元管理。'
  },
  {
    icon: BarChart3,
    title: 'レポーティング',
    description: 'プロジェクトの健全性、リソース配分、進捗状況を自動レポート化。'
  },
  {
    icon: Users,
    title: 'チーム管理',
    description: 'メンバーの負荷状況、スキルセット、稼働状況を可視化し、最適な配置を実現。'
  }
]

const benefits = [
  {
    title: '合意形成の高速化',
    description: '承認プロセスの可視化と自動化により、意思決定スピードが3倍に',
    icon: Zap,
    metric: '承認時間 70%削減'
  },
  {
    title: '情報の一元化',
    description: '散在していた情報を集約し、探す時間を大幅削減',
    icon: FileText,
    metric: '情報検索時間 80%削減'
  },
  {
    title: '透明性の向上',
    description: '全ステークホルダーが同じ情報を共有し、認識のズレを防止',
    icon: Shield,
    metric: '手戻り 60%削減'
  }
]

export function ProjectManagementContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/grid.svg')] opacity-20"></div>
        <div
          className="animate-fade-up container relative z-10"
        >
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">複数事業の並走管理</Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              プロジェクト管理
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              合意形成の摩擦を下げ、スピードを上げる
            </p>
            <p className="text-lg mb-8 opacity-80">
              タスク・成果物・合意を一元化。<br />
              カンバン、承認フロー、外部共有、レポーティングで<br />
              地域プロジェクトの成功確率を高めます。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" variant="secondary">
                無料トライアル開始
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                機能詳細を見る
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 100" className="w-full h-24 fill-background">
            <path d="M0,50 C360,100 1080,0 1440,50 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

      {/* 価値提案 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">
              地域プロジェクト特有の課題を解決
            </h2>
            <p className="text-lg text-muted-foreground">
              多様なステークホルダー、複雑な承認プロセス、頻繁な変更に対応
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card className="h-full">
                    <CardHeader>
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <CardTitle>{benefit.title}</CardTitle>
                      <Badge variant="secondary" className="mt-2">
                        {benefit.metric}
                      </Badge>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 主要機能 */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">主要機能</h2>
            <p className="text-lg text-muted-foreground">
              プロジェクトを成功に導く充実した機能群
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <FadeUp delay={index * 0.05}
                  key={index}
                >
                  <Card className="h-full">
                    <CardHeader>
                      <Icon className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 活用例 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">様々なプロジェクトに対応</h2>
            <p className="text-lg text-muted-foreground">
              地域の多様なプロジェクトで活用されています
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: 'スマートシティ推進プロジェクト',
                description: '複数部署、外部ベンダー、市民団体が参加する大規模プロジェクト',
                tasks: '250タスク',
                members: '45名',
                duration: '18ヶ月',
                result: '計画通り完遂、予算内で完了'
              },
              {
                title: '観光DX実証実験',
                description: '観光協会、地域事業者、IT企業による共同プロジェクト',
                tasks: '120タスク',
                members: '23名',
                duration: '6ヶ月',
                result: '実証実験から本格導入へ移行'
              },
              {
                title: '地域イベント運営',
                description: '実行委員会、ボランティア、協賛企業の連携プロジェクト',
                tasks: '180タスク',
                members: '67名',
                duration: '4ヶ月',
                result: '来場者数150%達成'
              },
              {
                title: '防災システム構築',
                description: '自治体、消防、警察、医療機関の連携システム構築',
                tasks: '320タスク',
                members: '38名',
                duration: '12ヶ月',
                result: '災害対応時間50%短縮'
              }
            ].map((example, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card>
                  <CardHeader>
                    <CardTitle>{example.title}</CardTitle>
                    <CardDescription>{example.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-muted-foreground">タスク数</p>
                        <p className="font-semibold">{example.tasks}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">参加者</p>
                        <p className="font-semibold">{example.members}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">期間</p>
                        <p className="font-semibold">{example.duration}</p>
                      </div>
                    </div>
                    <div className="pt-4 border-t">
                      <p className="text-sm font-semibold text-primary">{example.result}</p>
                    </div>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ワークフロー */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">承認フローの自動化</h2>
            <p className="text-lg text-muted-foreground">
              複雑な承認プロセスも視覚的に設計・運用
            </p>
          </FadeUp>

          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <div className="space-y-6">
                  {[
                    {
                      step: '申請',
                      description: 'フォームから簡単申請。必要書類も自動チェック',
                      icon: FileText
                    },
                    {
                      step: '一次承認',
                      description: '担当者に自動通知。条件に応じて自動承認も可能',
                      icon: CheckSquare
                    },
                    {
                      step: '二次承認',
                      description: '金額や内容に応じて適切な承認者へ自動ルーティング',
                      icon: Users
                    },
                    {
                      step: '最終承認',
                      description: '承認完了後、関係者への通知と次アクションを自動実行',
                      icon: CheckCircle2
                    }
                  ].map((flow, index) => {
                    const Icon = flow.icon
                    return (
                      <div key={index} className="flex items-center gap-4">
                        <div className="shrink-0">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                            <Icon className="h-6 w-6 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">{flow.step}</h4>
                          <p className="text-sm text-muted-foreground">{flow.description}</p>
                        </div>
                        {index < 3 && (
                          <ArrowRight className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 料金プラン */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">料金プラン</h2>
            <p className="text-lg text-muted-foreground">
              プロジェクト規模に応じた最適なプラン
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'ベーシック',
                price: '月額 3万円〜',
                description: '小規模プロジェクト向け',
                features: [
                  'プロジェクト数: 5個まで',
                  'ユーザー数: 10名まで',
                  'ストレージ: 10GB',
                  '基本的な承認フロー',
                  'メールサポート'
                ]
              },
              {
                name: 'プロフェッショナル',
                price: '月額 10万円〜',
                description: '中規模プロジェクト向け',
                features: [
                  'プロジェクト数: 無制限',
                  'ユーザー数: 50名まで',
                  'ストレージ: 100GB',
                  '高度な承認フロー',
                  'API連携',
                  '優先サポート'
                ],
                popular: true
              },
              {
                name: 'エンタープライズ',
                price: 'お問い合わせ',
                description: '大規模・複雑なプロジェクト向け',
                features: [
                  'プロジェクト数: 無制限',
                  'ユーザー数: 無制限',
                  'ストレージ: 無制限',
                  'カスタム承認フロー',
                  '専用インスタンス',
                  '専任サポート'
                ]
              }
            ].map((plan, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card className={`h-full ${plan.popular ? 'border-primary shadow-lg' : ''}`}>
                  {plan.popular && (
                    <div className="bg-primary text-white text-center py-2 text-sm font-semibold">
                      おすすめ
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="text-3xl font-bold mt-4">{plan.price}</div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6" variant={plan.popular ? 'default' : 'outline'}>
                      {plan.name === 'エンタープライズ' ? 'お問い合わせ' : '14日間無料トライアル'}
                    </Button>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <RefreshCw className="h-16 w-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold mb-4">
              プロジェクトの成功確率を、確実に上げる
            </h2>
            <p className="text-xl mb-8 opacity-90">
              14日間の無料トライアルで、プロジェクト管理の新しい形を体験
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary">
                無料トライアルを開始
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                デモを予約
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}